import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Fade,
  Collapse,
  Divider,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { keyframes } from "@mui/system";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import PdfVersionCard from "../../components/PackagingCheck/PdfVersionCard";
import CorrectionsEditor from "../../components/PackagingCheck/CorrectionsEditor";
import RecentChecks from "../../components/PackagingCheck/RecentChecks";
import ChecklistSettingsDialog from "../../components/PackagingCheck/ChecklistSettingsDialog";
import TabBar from "../../components/PackagingCheck/TabBar";
import usePackagingCheckApi, { getApiErrorMessage } from "../../components/PackagingCheck/usePackagingCheckApi";
import {
  BORDER,
  BRAND,
  BRAND_DARK,
  BRAND_SOFT,
  MAX_FILE_SIZE_BYTES,
  MAX_PAGES,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  VERSIONS,
} from "../../components/PackagingCheck/constants";

const EMPTY_PRODUCT = { name: "", sku: "", barcode: "" };
const OTHER_VERSION = { previous: "updated", updated: "previous" };
const TABS = [
  { key: "new", label: "בדיקה חדשה" },
  { key: "history", label: "בדיקות קודמות" },
];

const correctionKey = (text) => text.replace(/\s+/g, " ").trim();

const isSameFile = (a, b) =>
  a && b && a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

const popIn = keyframes`
  0% { transform: scale(0.6); }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

// Step number that turns into a green check once the step is filled
const StepBadge = ({ step, done }) => (
  <Box
    key={done ? "done" : "todo"}
    sx={{
      width: 26,
      height: 26,
      flexShrink: 0,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 13,
      fontWeight: 700,
      color: done ? "#fff" : BRAND_DARK,
      bgcolor: done ? BRAND : BRAND_SOFT,
      animation: done ? `${popIn} .35s ease-out` : "none",
      transition: "background-color .2s",
    }}
  >
    {done ? <CheckRoundedIcon sx={{ fontSize: 17 }} /> : step}
  </Box>
);

const Block = ({ step, done, title, optional, action, children }) => (
  <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.75, minHeight: 32 }}>
      <StepBadge step={step} done={done} />
      <Typography sx={{ fontWeight: 700, fontSize: 15.5, color: TEXT_PRIMARY }}>{title}</Typography>
      {optional && (
        <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
          · אופציונלי
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      {action}
    </Box>
    {children}
  </Box>
);

// Comments found in the previous PDF, offered as the corrections list
const ImportCommentsButton = ({ suggestions, corrections, onImport, disabled }) => {
  const existing = new Set(corrections.map((c) => correctionKey(c.text)));
  const pending = suggestions.filter((s) => !existing.has(correctionKey(s.text)));
  if (!suggestions.length) return null;
  if (!pending.length) {
    return (
      <Typography variant="caption" sx={{ color: BRAND_DARK, fontWeight: 600, display: "flex", alignItems: "center", gap: 0.5 }}>
        <CheckRoundedIcon sx={{ fontSize: 15 }} />
        ההערות מה-PDF יובאו
      </Typography>
    );
  }
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<CommentOutlinedIcon />}
      onClick={() => onImport(pending)}
      disabled={disabled}
      sx={{
        borderRadius: 1.5,
        fontWeight: 600,
        color: BRAND_DARK,
        borderColor: BRAND,
        "&:hover": { borderColor: BRAND_DARK, bgcolor: BRAND_SOFT },
      }}
    >
      ייבוא {pending.length} הערות מה-PDF
    </Button>
  );
};

const ProductDetails = ({ product, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const filled = [product.name, product.sku && `מק"ט ${product.sku}`, product.barcode].filter(Boolean).join(" · ");
  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          width: "100%",
          p: 0,
          border: "none",
          bgcolor: "transparent",
          font: "inherit",
          cursor: "pointer",
          textAlign: "start",
          minHeight: 36,
        }}
      >
        <StepBadge step={4} done={Boolean(filled)} />
        <Typography sx={{ fontWeight: 700, fontSize: 15.5, color: TEXT_PRIMARY }}>פרטי מוצר</Typography>
        <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
          · {filled || "אופציונלי — שם, מק״ט וברקוד"}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <ExpandMoreRoundedIcon sx={{ color: TEXT_SECONDARY, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </Box>
      <Collapse in={open}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" }, gap: 2, pt: 1.5, pb: 1 }}>
          {[
            { key: "name", label: "שם מוצר" },
            { key: "sku", label: 'מק"ט', ltr: true },
            { key: "barcode", label: "ברקוד", ltr: true },
          ].map(({ key, label, ltr }) => (
            <TextField
              key={key}
              size="small"
              label={label}
              value={product[key]}
              disabled={disabled}
              onChange={(e) => onChange({ ...product, [key]: e.target.value })}
              inputProps={ltr ? { dir: "ltr", style: { textAlign: "right" } } : undefined}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const PackagingCheckPage = () => {
  const api = usePackagingCheckApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "history" ? "history" : "new";
  const setTab = (key) => setSearchParams(key === "new" ? {} : { tab: key }, { replace: true });
  const openCheck = (id) => navigate(`/packaging-check/${id}`);

  const [files, setFiles] = useState({ previous: null, updated: null });
  const [title, setTitle] = useState("");
  const [product, setProduct] = useState(EMPTY_PRODUCT);
  const [corrections, setCorrections] = useState([]);
  const [submitState, setSubmitState] = useState({ loading: false, progress: 0, error: null });
  const [checklistOpen, setChecklistOpen] = useState(false);
  const abortControllers = useRef({});

  const recentChecks = useQuery({
    queryKey: ["packagingChecks"],
    queryFn: api.listChecks,
    staleTime: 30_000,
    // Refresh while a check is still running corrections control
    refetchInterval: (query) => (query.state.data?.some((c) => c.status === "controlling") ? 4000 : false),
  });

  useEffect(() => {
    const controllers = abortControllers.current;
    return () => Object.values(controllers).forEach((c) => c?.abort());
  }, []);

  const setEntry = (versionKey, entry) => setFiles((prev) => ({ ...prev, [versionKey]: entry }));

  const selectFile = useCallback(
    async (versionKey, file) => {
      abortControllers.current[versionKey]?.abort();
      setSubmitState((s) => ({ ...s, error: null }));

      const invalid = (error) => setEntry(versionKey, { file, status: "invalid", error });
      const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
      if (!isPdf) return invalid("יש להעלות קובץ PDF בלבד");
      if (file.size > MAX_FILE_SIZE_BYTES) return invalid("הקובץ גדול מ-50MB");
      if (isSameFile(file, files[OTHER_VERSION[versionKey]]?.file)) {
        return invalid(`זה אותו קובץ שנבחר כ${VERSIONS[OTHER_VERSION[versionKey]].label}`);
      }

      const controller = new AbortController();
      abortControllers.current[versionKey] = controller;
      setEntry(versionKey, { file, status: "validating" });
      try {
        const info = await api.validateFile(file, { signal: controller.signal });
        setEntry(versionKey, { file, status: "valid", info });
      } catch (error) {
        if (controller.signal.aborted) return;
        invalid(getApiErrorMessage(error, "לא ניתן לבדוק את הקובץ"));
      }
    },
    [api, files],
  );

  const clearFile = (versionKey) => {
    abortControllers.current[versionKey]?.abort();
    setEntry(versionKey, null);
  };

  const swapVersions = () => setFiles(({ previous, updated }) => ({ previous: updated, updated: previous }));

  const resetForm = () => {
    Object.values(abortControllers.current).forEach((c) => c?.abort());
    setFiles({ previous: null, updated: null });
    setTitle("");
    setProduct(EMPTY_PRODUCT);
    setCorrections([]);
    setSubmitState({ loading: false, progress: 0, error: null });
  };

  const bothValid = files.previous?.status === "valid" && files.updated?.status === "valid";
  const validCount = ["previous", "updated"].filter((k) => files[k]?.status === "valid").length;
  const anyValidating = ["previous", "updated"].some((k) => files[k]?.status === "validating");
  const cleanCorrections = corrections
    .map((c) => ({ ...c, text: c.text.trim() }))
    .filter((c) => c.text);
  const suggestedCorrections =
    files.previous?.status === "valid" ? files.previous.info.suggestedCorrections || [] : [];
  const isDirty = Boolean(
    title.trim() || files.previous || files.updated || cleanCorrections.length || Object.values(product).some(Boolean),
  );

  // Opens the results right away — they show the progress of the check
  const handleSubmit = async () => {
    setSubmitState({ loading: true, progress: 0, error: null });
    try {
      const check = await api.createCheck(
        {
          previousFile: files.previous.file,
          updatedFile: files.updated.file,
          title: title.trim(),
          product: {
            name: product.name.trim(),
            sku: product.sku.trim(),
            barcode: product.barcode.trim(),
          },
          corrections: cleanCorrections,
        },
        (e) => e.total && setSubmitState((s) => ({ ...s, progress: Math.round((e.loaded / e.total) * 100) })),
      );
      queryClient.invalidateQueries({ queryKey: ["packagingChecks"] });
      openCheck(check._id);
    } catch (error) {
      setSubmitState({ loading: false, progress: 0, error: getApiErrorMessage(error, "פתיחת הבדיקה נכשלה") });
    }
  };

  const busy = submitState.loading;
  const statusText = !files.previous && !files.updated
    ? "העלו את שתי הגרסאות כדי להתחיל"
    : `${validCount}/2 קבצים תקינים${cleanCorrections.length ? ` · ${cleanCorrections.length} תיקונים` : ""}`;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pt: { xs: 10, sm: 11 }, maxWidth: 1100, mx: "auto" }}>
      {/* Header */}
      <Fade in timeout={400}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, mb: 2.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="h1" sx={{ fontSize: 24, fontWeight: 700, color: TEXT_PRIMARY }}>
              בדיקת גרפיקות ד"ר עור
            </Typography>
            <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY, mt: 0.25 }}>
              השוואת גרסאות אריזה ובקרת יישום תיקונים
            </Typography>
          </Box>
          <Tooltip title="צ'ק-ליסט פריטי חובה">
            <Button
              startIcon={<PlaylistAddCheckIcon />}
              onClick={() => setChecklistOpen(true)}
              sx={{ borderRadius: 2, fontWeight: 600, color: TEXT_SECONDARY, display: { xs: "none", sm: "inline-flex" } }}
            >
              פריטי חובה
            </Button>
          </Tooltip>
        </Box>
      </Fade>
      <ChecklistSettingsDialog open={checklistOpen} onClose={() => setChecklistOpen(false)} />

      <Box sx={{ mb: 2 }}>
        <TabBar
          tabs={TABS}
          tab={tab}
          onChange={setTab}
          counts={{ history: recentChecks.data?.length ? { value: recentChecks.data.length } : null }}
        />
      </Box>

      {tab === "new" ? (
        <Fade in key="new" timeout={250}>
          <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${BORDER}` }}>
            <Block step={1} done={Boolean(title.trim())} title="שם הבדיקה" optional>
              <TextField
                fullWidth
                size="small"
                value={title}
                disabled={busy}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="לדוגמה: סרום לקרקפת – סבב תיקונים 3"
                inputProps={{ maxLength: 120, "aria-label": "שם הבדיקה" }}
                helperText="יוצג בבדיקות הקודמות ויאפשר לחפש את הבדיקה לפיו"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 15 } }}
              />
            </Block>

            <Divider />

            <Block step={2} done={bothValid} title="קבצי האריזה">
              <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "stretch", gap: { xs: 1.5, md: 2 } }}>
                <PdfVersionCard
                  version={VERSIONS.previous}
                  entry={files.previous}
                  onSelect={(file) => selectFile("previous", file)}
                  onClear={() => clearFile("previous")}
                  disabled={busy}
                />
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Tooltip title="החלפה בין הגרסאות">
                    <span>
                      <IconButton
                        onClick={swapVersions}
                        disabled={busy || anyValidating || (!files.previous && !files.updated)}
                        aria-label="החלפה בין הגרסאות"
                        sx={{
                          border: `1px solid ${BORDER}`,
                          bgcolor: "#fff",
                          transform: { xs: "rotate(90deg)", md: "none" },
                          "&:hover": { bgcolor: "#f8fafc" },
                        }}
                      >
                        <SwapHorizIcon sx={{ color: TEXT_SECONDARY }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                <PdfVersionCard
                  version={VERSIONS.updated}
                  entry={files.updated}
                  onSelect={(file) => selectFile("updated", file)}
                  onClear={() => clearFile("updated")}
                  disabled={busy}
                />
              </Box>
              <Typography sx={{ mt: 1.5, fontSize: 12.5, color: "#94a3b8", textAlign: "center" }}>
                PDF עם שכבת טקסט · עד {MAX_PAGES} עמודים · עד 50MB לקובץ
              </Typography>
            </Block>

            <Divider />

            <Block
              step={3}
              done={cleanCorrections.length > 0}
              title="תיקונים נדרשים"
              optional
              action={
                <ImportCommentsButton
                  suggestions={suggestedCorrections}
                  corrections={corrections}
                  onImport={(items) => setCorrections((prev) => [...prev, ...items])}
                  disabled={busy}
                />
              }
            >
              <CorrectionsEditor corrections={corrections} onChange={setCorrections} disabled={busy} />
            </Block>

            <Divider />

            <ProductDetails product={product} onChange={setProduct} disabled={busy} />

            {/* Action bar — stays visible at the bottom while scrolling */}
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                px: { xs: 2, sm: 3 },
                py: 1.75,
                bgcolor: "rgba(250, 251, 252, 0.94)",
                backdropFilter: "blur(6px)",
                borderTop: `1px solid ${BORDER}`,
                borderBottomLeftRadius: "12px",
                borderBottomRightRadius: "12px",
              }}
            >
              <Collapse in={Boolean(submitState.error)}>
                <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }} onClose={() => setSubmitState((s) => ({ ...s, error: null }))}>
                  {submitState.error}
                </Alert>
              </Collapse>
              {busy && (
                <Box sx={{ mb: 1.5 }}>
                  <LinearProgress
                    variant={submitState.progress < 100 ? "determinate" : "indeterminate"}
                    value={submitState.progress}
                    sx={{ borderRadius: 1, height: 6 }}
                  />
                  <Typography variant="caption" color={TEXT_SECONDARY} sx={{ mt: 0.75, display: "block" }}>
                    {submitState.progress < 100 ? `מעלה קבצים… ${submitState.progress}%` : "פותח את הבדיקה…"}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Typography variant="body2" color={TEXT_SECONDARY} sx={{ flex: 1, minWidth: 180 }}>
                  {statusText}
                </Typography>
                <Button onClick={resetForm} disabled={busy || !isDirty} sx={{ color: TEXT_SECONDARY, fontWeight: 600 }}>
                  ניקוי
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  size="large"
                  startIcon={<PlayArrowRoundedIcon />}
                  onClick={handleSubmit}
                  disabled={!bothValid || anyValidating || busy}
                  sx={{
                    borderRadius: 2,
                    px: 3.5,
                    minHeight: 46,
                    fontSize: 15.5,
                    fontWeight: 700,
                    bgcolor: BRAND,
                    // "Lights up" once both files are ready
                    boxShadow: bothValid && !busy ? `0 6px 18px ${BRAND}40` : "none",
                    transition: "transform .15s, box-shadow .2s, background-color .2s",
                    "&:hover": { bgcolor: BRAND_DARK, transform: "translateY(-1px)", boxShadow: `0 8px 22px ${BRAND}55` },
                  }}
                >
                  הפעלת בדיקה
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      ) : (
        <Fade in key="history" timeout={250}>
          <Box>
            <RecentChecks
              checks={recentChecks.data}
              isLoading={recentChecks.isLoading}
              error={recentChecks.error}
              onSelect={openCheck}
            />
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default PackagingCheckPage;
